import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Download, Search, Users, Calendar, Mail, Phone, Building, Briefcase } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const WaitlistManager = () => {
  const [waitlist, setWaitlist] = useState([]);
  const [filteredWaitlist, setFilteredWaitlist] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    loadWaitlist();
  }, []);

  useEffect(() => {
    // Filter waitlist based on search term
    if (searchTerm.trim() === '') {
      setFilteredWaitlist(waitlist);
    } else {
      const term = searchTerm.toLowerCase();
      const filtered = waitlist.filter(entry => 
        entry.name?.toLowerCase().includes(term) ||
        entry.email?.toLowerCase().includes(term) ||
        entry.company?.toLowerCase().includes(term) ||
        entry.role?.toLowerCase().includes(term)
      );
      setFilteredWaitlist(filtered);
    }
  }, [searchTerm, waitlist]);

  const loadWaitlist = async () => {
    try {
      const response = await axios.get(`${API}/waitlist`, { withCredentials: true });
      // Sort by created_at descending (newest first)
      const sorted = response.data.sort((a, b) => 
        new Date(b.created_at) - new Date(a.created_at)
      );
      setWaitlist(sorted);
      setFilteredWaitlist(sorted);
    } catch (error) {
      console.error('Error loading waitlist:', error);
      if (error.response?.status === 403) {
        toast.error('Admin access required to view waitlist');
      } else {
        toast.error('Failed to load waitlist');
      }
    } finally {
      setLoading(false);
    }
  };

  const exportToCSV = () => {
    if (filteredWaitlist.length === 0) {
      toast.error('No data to export');
      return;
    }

    // Create CSV content
    const headers = ['Name', 'Email', 'Phone', 'Company', 'Role', 'Joined Date'];
    const rows = filteredWaitlist.map(entry => [
      entry.name || '',
      entry.email || '',
      entry.phone || '',
      entry.company || '',
      entry.role || '',
      new Date(entry.created_at).toLocaleString()
    ]);

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');

    // Download CSV
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `waitlist-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);

    toast.success('Waitlist exported successfully');
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-12">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto mb-4"></div>
          <p className="text-slate-600">Loading waitlist...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-slate-600 flex items-center gap-2">
              <Users className="w-4 h-4" />
              Total Signups
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-indigo-600">{waitlist.length}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-slate-600 flex items-center gap-2">
              <Calendar className="w-4 h-4" />
              Last 7 Days
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-cyan-600">
              {waitlist.filter(e => {
                const daysDiff = (new Date() - new Date(e.created_at)) / (1000 * 60 * 60 * 24);
                return daysDiff <= 7;
              }).length}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-slate-600 flex items-center gap-2">
              <Building className="w-4 h-4" />
              With Company
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-purple-600">
              {waitlist.filter(e => e.company && e.company.trim() !== '').length}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-slate-600 flex items-center gap-2">
              <Phone className="w-4 h-4" />
              With Phone
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-green-600">
              {waitlist.filter(e => e.phone && e.phone.trim() !== '').length}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Search and Export */}
      <Card>
        <CardHeader>
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
            <div>
              <CardTitle>Waitlist Entries</CardTitle>
              <CardDescription>
                {filteredWaitlist.length} {filteredWaitlist.length === 1 ? 'entry' : 'entries'}
                {searchTerm && ` (filtered from ${waitlist.length})`}
              </CardDescription>
            </div>
            <div className="flex gap-2 w-full sm:w-auto">
              <div className="relative flex-1 sm:flex-initial">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-4 h-4" />
                <Input
                  placeholder="Search by name, email, company..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 w-full sm:w-64"
                />
              </div>
              <Button onClick={exportToCSV} variant="outline" className="gap-2">
                <Download className="w-4 h-4" />
                Export CSV
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {filteredWaitlist.length === 0 ? (
            <div className="text-center py-12">
              <Users className="w-12 h-12 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-500">
                {searchTerm ? 'No entries match your search' : 'No waitlist entries yet'}
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr>
                    <th className="text-left p-3 font-semibold text-slate-700">Name</th>
                    <th className="text-left p-3 font-semibold text-slate-700">Email</th>
                    <th className="text-left p-3 font-semibold text-slate-700">Phone</th>
                    <th className="text-left p-3 font-semibold text-slate-700">Company</th>
                    <th className="text-left p-3 font-semibold text-slate-700">Role</th>
                    <th className="text-left p-3 font-semibold text-slate-700">Joined</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredWaitlist.map((entry, index) => (
                    <tr 
                      key={entry.id || index} 
                      className="border-b border-slate-100 hover:bg-slate-50 transition-colors"
                    >
                      <td className="p-3">
                        <div className="flex items-center gap-2">
                          <div className="w-8 h-8 rounded-full bg-indigo-100 flex items-center justify-center text-indigo-600 font-semibold text-xs">
                            {entry.name?.charAt(0).toUpperCase() || '?'}
                          </div>
                          <span className="font-medium text-slate-800">{entry.name || '-'}</span>
                        </div>
                      </td>
                      <td className="p-3">
                        <div className="flex items-center gap-2 text-slate-600">
                          <Mail className="w-4 h-4 text-slate-400" />
                          <a href={`mailto:${entry.email}`} className="hover:text-indigo-600 hover:underline">
                            {entry.email || '-'}
                          </a>
                        </div>
                      </td>
                      <td className="p-3">
                        <div className="flex items-center gap-2 text-slate-600">
                          <Phone className="w-4 h-4 text-slate-400" />
                          {entry.phone || '-'}
                        </div>
                      </td>
                      <td className="p-3">
                        <div className="flex items-center gap-2 text-slate-600">
                          <Building className="w-4 h-4 text-slate-400" />
                          {entry.company || '-'}
                        </div>
                      </td>
                      <td className="p-3">
                        <div className="flex items-center gap-2 text-slate-600">
                          <Briefcase className="w-4 h-4 text-slate-400" />
                          {entry.role || '-'}
                        </div>
                      </td>
                      <td className="p-3 text-slate-600 whitespace-nowrap">
                        <div className="flex items-center gap-2">
                          <Calendar className="w-4 h-4 text-slate-400" />
                          {formatDate(entry.created_at)}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default WaitlistManager;
